import { actionTypes } from "../constant/action-types";
const initialState = {
  grouplist: [],
  contactList: [],
  messagesList: [],
  allMessageDatas: [],
  lastmessageIds: [],
  selectedChat: null,
};

const byTime = (a, b) => new Date(a.time) - new Date(b.time);

// Messages in `incoming` replace same-_id ones in `existing`; result is
// oldest-first.
const mergeMessages = (existing, incoming) => {
  const incomingIds = new Set(incoming.map((m) => m._id));
  return [...existing.filter((m) => !incomingIds.has(m._id)), ...incoming].sort(byTime);
};

// Opening a chat marks all of the reader's receipts in it seen at once
// (markConversationAsSeen), so once a message of ours is read every earlier
// one was too — this gives older loaded messages their blue tick without
// refetching them.
const propagateSeen = (messages) => {
  let seenUpTo = -Infinity;
  for (const m of messages) {
    if (m.is_mine && m.status === "seen") seenUpTo = Math.max(seenUpTo, new Date(m.time).getTime());
  }
  if (seenUpTo === -Infinity) return messages;
  return messages.map((m) =>
    m.is_mine && m.status !== "seen" && new Date(m.time).getTime() <= seenUpTo
      ? { ...m, status: "seen" }
      : m,
  );
};

// Pages for a conversation that's no longer open are dropped — the request
// raced a switch to another chat.
const isOpenConversation = (state, payload) =>
  !!state.messagesList?.conversation_id &&
  state.messagesList.conversation_id === payload.conversation_id;

export const ChatReducers = (state = initialState, { type, payload }) => {
  switch (type) {
    case actionTypes.SAVE_GROUPS:
      return { ...state, grouplist: [...payload] };
    case actionTypes.SAVE_CONTACT_LIST:
      return { ...state, contactList: [...payload] };
    case actionTypes.SAVE_MESSAGES:
      return { ...state, messagesList: payload };
    case actionTypes.PREPEND_MESSAGES: {
      if (!isOpenConversation(state, payload)) return state;
      const existingMessages = state.messagesList?.messages || [];
      return {
        ...state,
        messagesList: {
          ...state.messagesList,
          messages: mergeMessages(existingMessages, payload.messages || []),
          // Only the oldest page knows whether anything older remains.
          olderPagination: payload.pagination,
        },
      };
    }
    case actionTypes.MERGE_LATEST_MESSAGES: {
      if (!isOpenConversation(state, payload)) return state;
      const existingMessages = state.messagesList?.messages || [];
      const fresh = payload.messages || [];
      const existingIds = new Set(existingMessages.map((m) => m._id));
      // Nothing in common and more beyond it: more arrived than one page
      // holds, so stitching would leave a hole. Start over from the fresh page.
      if (!fresh.some((m) => existingIds.has(m._id)) && payload.pagination?.hasMore) {
        return { ...state, messagesList: payload };
      }
      return {
        ...state,
        messagesList: {
          ...state.messagesList,
          teamDetails: payload.teamDetails ?? state.messagesList.teamDetails,
          messages: propagateSeen(mergeMessages(existingMessages, fresh)),
        },
      };
    }
    case actionTypes.PATCH_MESSAGE: {
      if (!isOpenConversation(state, payload)) return state;
      return {
        ...state,
        messagesList: {
          ...state.messagesList,
          messages: (state.messagesList.messages || []).map((m) =>
            m._id === payload.message_id ? { ...m, ...payload.patch } : m,
          ),
        },
      };
    }
    case actionTypes.SAVE_RECEIVED_MESSAGES:
      return { ...state, allMessageDatas: payload };
    case actionTypes.SAVE_LAST_MESSAGE_IDS:
      return { ...state, lastmessageIds: payload };
    case actionTypes.SELECTED_CHAT_MEMBER:
      return { ...state, selectedChat: payload };
    default:
      return state;
  }
};
